import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Shield, AlertCircle } from 'lucide-react';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:5000/api' : 'https://e2b-backend.onrender.com/api');

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback,
  requireAuth = true,
  requireAdmin = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, accessToken, refreshToken, setAuth } = useAuthStore();
  const [restoring, setRestoring] = useState(false);

  // 토큰은 있는데 스토어에 user가 없을 때(새로고침/직접 접속) /users/me로 복구
  useEffect(() => {
    if (!requireAdmin || user || !accessToken || restoring) return;
    const token = accessToken || localStorage.getItem('accessToken');
    const refToken = refreshToken || localStorage.getItem('refreshToken');
    if (!token) return;

    let cancelled = false;
    setRestoring(true);
    fetch(`${API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('unauthorized'))))
      .then((me) => {
        if (cancelled) return;
        setAuth(
          {
            id: me.id,
            email: me.email,
            name: me.name,
            role: me.role || 'MEMBER',
            avatar: me.avatar,
            provider: (me.provider as 'email' | 'kakao') || 'email',
            mustChangePassword: Boolean(me.mustChangePassword),
          },
          token,
          refToken || token
        );
      })
      .catch(() => { if (!cancelled) setRestoring(false); })
      .finally(() => { if (!cancelled) setRestoring(false); });

    return () => { cancelled = true; };
  }, [requireAdmin, user, accessToken, refreshToken, setAuth, restoring]);

  if (isLoading || (requireAdmin && !user && accessToken && restoring)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg animate-pulse">
            <span className="text-white font-bold text-2xl" style={{ fontFamily: 'cursive' }}>e</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">로딩 중...</h2>
          <div className="flex justify-center space-x-2">
            <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-150"></div>
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce delay-300"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && user?.mustChangePassword && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }

  // 관리자 권한 체크
  if (requireAdmin) {
    if (!isAuthenticated || !user) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-red-100 rounded-full">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">로그인이 필요합니다</h2>
            <p className="text-gray-600 mb-4">관리자 페이지에 접근하려면 먼저 로그인해주세요.</p>
            <button
              onClick={() => navigate('/admin-login')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              관리자 로그인
            </button>
          </div>
        </div>
      );
    }

    if (user.role !== 'ADMIN') {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-red-100 rounded-full">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">접근 권한이 없습니다</h2>
            <p className="text-gray-600 mb-4">관리자 권한이 필요한 페이지입니다.</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                메인으로
              </button>
              <button
                onClick={() => navigate('/admin-login')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                관리자 로그인
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // 일반 인증 체크
  if (requireAuth && !isAuthenticated) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl" style={{ fontFamily: 'cursive' }}>e</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">로그인이 필요합니다</h2>
          <p className="text-slate-600 mb-6">이 기능을 사용하려면 먼저 로그인해주세요.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg"
          >
            로그인하기
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
